<template>
  <div>
    <v-container fluid>
      <v-alert
        v-if="errorMessage"
        type="error"
        dismissible
        @dismiss="errorMessage = ''"
        class="mb-4"
      >
        {{ errorMessage }}
      </v-alert>

      <v-card class="pa-5 mb-5" elevation="4">
        <v-row align="center" justify="space-between">
          <v-col cols="12" md="4" lg="3">
            <v-menu
              v-model="menuInicio"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedInicio"
                  label="Data de Início"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  outlined
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.inicio"
                @input="menuInicio = false"
                locale="pt-BR"
                class="custom-calendar"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="4" lg="3">
            <v-menu
              v-model="menuFim"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedFim"
                  label="Data de Fim"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  outlined
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.fim"
                @input="menuFim = false"
                locale="pt-BR"
                class="custom-calendar"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="4" lg="2">
            <v-btn
              color="primary"
              @click="fetchData"
              :loading="loading"
              :disabled="loading"
              class="search-button"
              elevation="2"
              block
            >
              <v-icon left>mdi-magnify</v-icon>
              Recarregar
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <v-card class="pa-3" elevation="4">
        <v-data-table
          :headers="headers"
          :items="filteredCertificados"
          :loading="loading"
          class="elevation-1"
          :items-per-page="10"
          :no-data-text="'Nenhum certificado encontrado.'"
          dense
          :footer-props="{
            'itemsPerPageText': 'Itens por página:',
            'itemsPerPageOptions': [10, 20, 30, 40],
            'showFirstLastPage': true,
            'pageText': '{0}-{1} de {2}'
          }"
        >
          <template v-slot:top>
            <v-row class="px-4 py-2" align="center" justify="space-between">
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filters.cnpj"
                  label="Pesquisar CNPJ"
                  clearable
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="filters.razaoSocial"
                  label="Pesquisar Razão Social"
                  clearable
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filters.status"
                  :items="['Todos', 'Ativo', 'Inativo']"
                  label="Filtrar por Status"
                  clearable
                  outlined
                  dense
                ></v-select>
              </v-col>
            </v-row>
          </template>

          <template v-slot:item.cnpj="{ item }">
            <span>{{ formatCNPJ(item.cnpj) }}</span>
          </template>

          <template v-slot:item.razao="{ item }">
            <span>{{ item.razao }}</span>
          </template>

          <template v-slot:item.status="{ item }">
            <span :class="{
              'text-ativo': item.status === 'ATIVO',
              'text-inativo': item.status === 'INATIVO'
            }">
              {{ item.status }}
            </span>
          </template>

          <template v-slot:item.dataExpiracao="{ item }">
            <span>{{ formatDate(item.dataExpiracao) }}</span>
          </template>
        </v-data-table>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import Certificado from '../service/certificado'

export default {
  name: 'CertificadosDigitais',
  data() {
    return {
      filters: {
        inicio: null,
        fim: null,
        status: 'Todos',
        cnpj: '',
        razaoSocial: '',
      },
      menuInicio: false,
      menuFim: false,
      headers: [
        { text: 'CNPJ', value: 'cnpj' },
        { text: 'Razão Social', value: 'razao' },
        { text: 'Status', value: 'status' },
        { text: 'Data Expiração', value: 'dataExpiracao' },
      ],
      certificados: [],
      loading: false,
      errorMessage: '',
    }
  },
  computed: {
    formattedInicio() {
      if (!this.filters.inicio) return ''
      const [year, month, day] = this.filters.inicio.split('-')
      return `${day}/${month}/${year}`
    },
    formattedFim() {
      if (!this.filters.fim) return ''
      const [year, month, day] = this.filters.fim.split('-')
      return `${day}/${month}/${year}`
    },
    filteredCertificados() {
      return this.certificados.filter(cert => {
        const matchesCnpj = cert.cnpj.includes(this.filters.cnpj || '')
        const matchesRazaoSocial = cert.razao.toLowerCase().includes(this.filters.razaoSocial.toLowerCase() || '')
        const matchesStatus =
          this.filters.status === 'Todos' || cert.status === this.filters.status.toUpperCase()
        
        // Filtrar por período de data localmente
        const certDate = new Date(cert.dataExpiracao)
        const startDate = this.filters.inicio ? new Date(this.filters.inicio) : null
        const endDate = this.filters.fim ? new Date(this.filters.fim) : null
        
        const matchesDate = (!startDate || certDate >= startDate) && (!endDate || certDate <= endDate)

        return matchesCnpj && matchesRazaoSocial && matchesStatus && matchesDate
      })
    },
  },
  created() {
    this.fetchData() // Carrega os dados automaticamente ao criar o componente
  },
  methods: {
    formatCNPJ(cnpj) {
      return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
    },
    formatDate(dateStr) {
      const date = new Date(dateStr)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    },
    getOneYearFromNow() {
      const date = new Date()
      date.setFullYear(date.getFullYear() + 1)
      return date.toISOString().split('T')[0] // Retorna formato YYYY-MM-DD
    },
    async fetchData() {
      this.errorMessage = ''
      const vencimento = this.getOneYearFromNow()
      const idUsuario = localStorage.getItem("id")

      if (!idUsuario) {
        this.errorMessage = 'Erro ao obter o ID do usuário.'
        return
      }

      this.loading = true
      try {
        const response = await Certificado.getCertificados(vencimento, idUsuario)
        this.certificados = response.data
      } catch (error) {
        this.errorMessage = 'Erro ao buscar os certificados.'
        console.error(error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.v-text-field {
  background-color: white;
}

.v-data-table {
  background-color: white;
}

.search-button {
  min-width: 150px;
  padding: 8px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background-color: #1976D2;
  transition: background-color 0.3s ease;
}

.search-button:hover {
  background-color: #1565C0;
}

.text-ativo {
  color: #388e3c;
  font-weight: bold;
}

.text-inativo {
  color: #d32f2f;
  font-weight: bold;
}

.custom-calendar {
  background-color: #f5f5f5;
  color: #212121;
}

.v-select {
  background-color: white;
}

.v-text-field input {
  color: #424242;
}

@media (max-width: 600px) {
  .v-data-table {
    overflow-x: auto;
  }
}
</style>
